import { fridayInfo, pacificNowParts } from "@/lib/fridays";

/** Add n days to a YYYY-MM-DD string (calendar math, no timezone drift). */
export function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return dt.toISOString().slice(0, 10);
}

function weekday(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** Epoch ms for a Pacific wall-clock date + time ("HH:MM"), DST-safe. */
export function pacificEpoch(dateStr, time = "12:00") {
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  let guess = Date.UTC(y, m - 1, d, hh, mm);
  for (let i = 0; i < 2; i += 1) {
    const p = pacificNowParts(new Date(guess));
    const [py, pm, pd] = p.date.split("-").map(Number);
    const [ph, pmin] = p.time.split(":").map(Number);
    const asUtc = Date.UTC(py, pm - 1, pd, ph, pmin);
    guess += Date.UTC(y, m - 1, d, hh, mm) - asUtc;
  }
  return guess;
}

/**
 * Work out the next public Friday gathering.
 * Returns { date, ordinal, location, event, startTime, endTime, skippedCoreWeek } or null.
 * - Skips 5th Fridays (Core team week) but remembers that it did.
 * - Prefers a published event on that date; otherwise falls back to the venue rotation.
 * - If the earliest upcoming event is sooner than the computed Friday (special date), it wins.
 */
export function nextFriday({ events = [], locations = [], now = new Date() } = {}) {
  const { date: today, time } = pacificNowParts(now);
  const byOrdinal = new Map(locations.map((l) => [l.friday_ordinal, l]));
  let cursor = today;
  let skippedCoreWeek = null;
  for (let i = 0; i < 70; i += 1) {
    if (weekday(cursor) === 5) {
      const ev = events.find((e) => e.date === cursor);
      const { ordinal } = fridayInfo(cursor);
      const loc = ev?.location || byOrdinal.get(ordinal) || null;
      const endTime = ev?.end_time || loc?.end_time || "15:00";
      const ended = cursor === today && time >= endTime;
      if (!ended) {
        if (ordinal === 5 && !ev) {
          skippedCoreWeek = cursor;
        } else {
          return {
            date: cursor, ordinal, event: ev || null,
            location: loc,
            startTime: ev?.start_time || loc?.start_time || "12:00", endTime, skippedCoreWeek,
          };
        }
      }
    }
    cursor = addDays(cursor, 1);
  }
  return null;
}

/** Long, friendly date: "Friday, September 4". */
export function longDate(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" });
}

/** Short date: "Fri, Sep 4". */
export function shortDate(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
}
